import { api } from './axios';

export type Medium = 'ENGLISH' | 'URDU';

export interface PeriodSlot {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  sortOrder: number;
  active: boolean;
}

export interface PeriodSlotInput {
  name: string;
  startTime: string;
  endTime: string;
  sortOrder: number;
}

export interface Band {
  min_pct: number;
  max_pct: number;
  grade: string;
  remarks?: string | null;
}

export interface GradingScale {
  id: number;
  name: string;
  bands: Band[];
  active: boolean;
}

export interface GradingScaleInput {
  name: string;
  bands: Band[];
}

export interface ClassSubject {
  id: number;
  classSectionId: number;
  subjectId: number;
  subjectName: string | null;
  compulsory: boolean;
  medium: Medium;
  hasPractical: boolean;
  theoryTotalMarks: number;
  theoryPassingMarks: number;
  practicalTotalMarks: number | null;
  practicalPassingMarks: number | null;
  mustPassBoth: boolean;
}

export interface ClassSubjectInput {
  subjectId: number;
  compulsory: boolean;
  medium: Medium;
  hasPractical: boolean;
  theoryTotalMarks: number;
  theoryPassingMarks: number;
  practicalTotalMarks?: number | null;
  practicalPassingMarks?: number | null;
  mustPassBoth: boolean;
}

export interface BulkClassSubjectResult {
  created: number;
  skipped: number;
  skippedSubjects: string[];
}

export interface ExamTypeWeightage {
  id: number;
  classId: number;
  className: string | null;
  examTypeId: number;
  examTypeName: string | null;
  weightagePct: string;
  active: boolean;
}

export interface ExamTypeWeightageInput {
  classId: number;
  examTypeId: number;
  weightagePct: string;
}

export interface TimetableEntry {
  id: number;
  classSectionId: number;
  classSectionName: string | null;
  periodSlotId: number;
  periodSlotName: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  dayOfWeek: number;
  subjectId: number;
  subjectName: string | null;
  teacherId: number;
  teacherName: string | null;
}

export interface TimetableEntryInput {
  classSectionId: number;
  periodSlotId: number;
  dayOfWeek: number;
  subjectId: number;
  teacherId: number;
}

export interface ExamSchedule {
  id: number;
  classSectionId: number;
  classSectionName: string | null;
  examTypeId: number;
  examTypeName: string | null;
  subjectId: number;
  subjectName: string | null;
  examDate: string;
  totalMarks: number;
  passingMarks: number;
}

export interface ExamScheduleInput {
  classSectionId: number;
  examTypeId: number;
  subjectId: number;
  examDate: string;
  totalMarks: number;
  passingMarks: number;
}

export interface MarkRow {
  studentMarkId: number | null;
  enrollmentId: number;
  registrationNumber: string | null;
  studentName: string | null;
  absent: boolean;
  theoryMarks: string | null;
  practicalMarks: string | null;
}

export interface MarksSheet {
  examScheduleId: number;
  classSectionName: string | null;
  examTypeName: string | null;
  subjectName: string | null;
  examDate: string;
  totalMarks: number;
  passingMarks: number;
  hasPractical: boolean;
  practicalTotalMarks: number | null;
  practicalPassingMarks: number | null;
  rows: MarkRow[];
}

export interface SaveMarksInput {
  examScheduleId: number;
  marks: {
    enrollmentId: number;
    absent: boolean;
    theoryMarks?: string | null;
    practicalMarks?: string | null;
  }[];
}

export interface ResultRow {
  enrollmentId: number;
  registrationNumber: string | null;
  studentName: string | null;
  weightedTotal: string | null;
  percentage: string | null;
  grade: string | null;
  pass: boolean | null;
  classPosition: number | null;
  published: boolean;
}

export interface SubjectResult {
  subjectId: number;
  subjectName: string | null;
  weightedTotal: string | null;
  percentage: string | null;
  grade: string | null;
  pass: boolean | null;
  classPosition: number | null;
  published: boolean;
}

export interface ReportCard {
  enrollmentId: number;
  registrationNumber: string | null;
  studentName: string | null;
  classSectionName: string | null;
  academicYearName: string | null;
  aggregatePercentage: string | null;
  aggregateGrade: string | null;
  overallPass: boolean | null;
  calculatedAt: string | null;
  allPublished: boolean;
  subjects: SubjectResult[];
}

export const academicsApi = {
  // period slots
  listPeriodSlots: (activeOnly = false) =>
    api.get<PeriodSlot[]>('/academics/period-slots', { params: { activeOnly } }).then((r) => r.data),
  createPeriodSlot: (body: PeriodSlotInput) =>
    api.post<PeriodSlot>('/academics/period-slots', body).then((r) => r.data),
  updatePeriodSlot: (id: number, body: PeriodSlotInput) =>
    api.put<PeriodSlot>(`/academics/period-slots/${id}`, body).then((r) => r.data),
  togglePeriodSlot: (id: number, value: boolean) =>
    api
      .patch<PeriodSlot>(`/academics/period-slots/${id}/active`, null, { params: { value } })
      .then((r) => r.data),

  // grading scales
  listGradingScales: () => api.get<GradingScale[]>('/academics/grading-scales').then((r) => r.data),
  createGradingScale: (body: GradingScaleInput) =>
    api.post<GradingScale>('/academics/grading-scales', body).then((r) => r.data),
  updateGradingScale: (id: number, body: GradingScaleInput) =>
    api.put<GradingScale>(`/academics/grading-scales/${id}`, body).then((r) => r.data),
  activateGradingScale: (id: number) =>
    api.patch<GradingScale>(`/academics/grading-scales/${id}/activate`).then((r) => r.data),

  // class subjects
  listClassSubjects: (classSectionId: number) =>
    api.get<ClassSubject[]>(`/academics/sections/${classSectionId}/subjects`).then((r) => r.data),
  createClassSubject: (classSectionId: number, body: ClassSubjectInput) =>
    api.post<ClassSubject>(`/academics/sections/${classSectionId}/subjects`, body).then((r) => r.data),
  updateClassSubject: (id: number, body: ClassSubjectInput) =>
    api.put<ClassSubject>(`/academics/class-subjects/${id}`, body).then((r) => r.data),
  deleteClassSubject: (id: number) =>
    api.delete<void>(`/academics/class-subjects/${id}`).then((r) => r.data),
  bulkCreateClassSubjects: (
    classSectionId: number,
    subjects: ClassSubjectInput[],
  ) =>
    api
      .post<BulkClassSubjectResult>(`/academics/sections/${classSectionId}/subjects/bulk`, { subjects })
      .then((r) => r.data),
  copyClassSubjects: (classSectionId: number, sourceClassSectionId: number) =>
    api
      .post<BulkClassSubjectResult>(`/academics/sections/${classSectionId}/subjects/copy-from`, {
        sourceClassSectionId,
      })
      .then((r) => r.data),

  // weightages
  listWeightages: (classId: number) =>
    api.get<ExamTypeWeightage[]>(`/academics/classes/${classId}/weightages`).then((r) => r.data),
  createWeightage: (body: ExamTypeWeightageInput) =>
    api.post<ExamTypeWeightage>('/academics/weightages', body).then((r) => r.data),
  updateWeightage: (id: number, body: ExamTypeWeightageInput) =>
    api.put<ExamTypeWeightage>(`/academics/weightages/${id}`, body).then((r) => r.data),
  toggleWeightage: (id: number, value: boolean) =>
    api
      .patch<ExamTypeWeightage>(`/academics/weightages/${id}/active`, null, { params: { value } })
      .then((r) => r.data),
  deleteWeightage: (id: number) =>
    api.delete<void>(`/academics/weightages/${id}`).then((r) => r.data),

  // timetable
  sectionTimetable: (classSectionId: number) =>
    api.get<TimetableEntry[]>(`/academics/sections/${classSectionId}/timetable`).then((r) => r.data),
  teacherTimetable: (teacherId: number) =>
    api.get<TimetableEntry[]>(`/academics/teachers/${teacherId}/timetable`).then((r) => r.data),
  upsertTimetable: (body: TimetableEntryInput) =>
    api.post<TimetableEntry>('/academics/timetable', body).then((r) => r.data),
  deleteTimetable: (id: number) =>
    api.delete<void>(`/academics/timetable/${id}`).then((r) => r.data),

  // exam schedule
  examSchedule: (classSectionId: number, examTypeId?: number) =>
    api
      .get<ExamSchedule[]>(`/academics/sections/${classSectionId}/exam-schedule`, { params: { examTypeId } })
      .then((r) => r.data),
  createExamSchedule: (body: ExamScheduleInput) =>
    api.post<ExamSchedule>('/academics/exam-schedule', body).then((r) => r.data),
  updateExamSchedule: (id: number, body: ExamScheduleInput) =>
    api.put<ExamSchedule>(`/academics/exam-schedule/${id}`, body).then((r) => r.data),
  deleteExamSchedule: (id: number) =>
    api.delete<void>(`/academics/exam-schedule/${id}`).then((r) => r.data),

  // marks
  marksSheet: (examScheduleId: number) =>
    api.get<MarksSheet>('/academics/marks/sheet', { params: { examScheduleId } }).then((r) => r.data),
  saveMarks: (body: SaveMarksInput) =>
    api.post<MarksSheet>('/academics/marks', body).then((r) => r.data),

  // results
  resultsForSection: (classSectionId: number, subjectId: number) =>
    api
      .get<ResultRow[]>('/academics/results', { params: { classSectionId, subjectId } })
      .then((r) => r.data),
  calculateResults: (classSectionId: number) =>
    api.post<number>('/academics/results/calculate', { classSectionId }).then((r) => r.data),
  publishResults: (classSectionId: number, publish: boolean) =>
    api.post<number>('/academics/results/publish', { classSectionId, publish }).then((r) => r.data),
  reportCard: (enrollmentId: number) =>
    api.get<ReportCard>(`/academics/enrollments/${enrollmentId}/report-card`).then((r) => r.data),

  // promotions
  previewPromotion: (fromAcademicYearId: number, toAcademicYearId: number) =>
    api
      .get<PromotionPreviewResponse>('/academics/promotions/preview', {
        params: { fromAcademicYearId, toAcademicYearId },
      })
      .then((r) => r.data),
  executePromotion: (body: ExecutePromotionsRequest) =>
    api.post<ExecutePromotionsResult>('/academics/promotions/execute', body).then((r) => r.data),
  promotionHistory: (studentId: number) =>
    api.get<PromotionHistoryRow[]>(`/academics/promotions/student/${studentId}`).then((r) => r.data),
};

export type PromotionDecision = 'PROMOTE' | 'REPEAT' | 'SKIP' | 'GRADUATE' | 'LEFT';

export interface ClassSectionLite {
  id: number;
  name: string;
  classId: number;
  classSortOrder: number;
}

export interface PromotionPreviewRow {
  studentId: number;
  registrationNumber: string;
  studentName: string;
  fromEnrollmentId: number;
  fromClassSectionId: number;
  fromClassSectionName: string;
  suggestedDecision: PromotionDecision;
  suggestedClassSectionId: number | null;
  suggestedClassSectionName: string | null;
}

export interface PromotionPreviewResponse {
  fromAcademicYearId: number;
  toAcademicYearId: number;
  targetSections: ClassSectionLite[];
  rows: PromotionPreviewRow[];
}

export interface PromotionDecisionRequest {
  studentId: number;
  decision: PromotionDecision;
  targetClassSectionId?: number | null;
  reason?: string | null;
}

export interface ExecutePromotionsRequest {
  fromAcademicYearId: number;
  toAcademicYearId: number;
  decisions: PromotionDecisionRequest[];
}

export interface PromotionResultRow {
  studentId: number;
  studentName: string;
  decision: PromotionDecision;
  success: boolean;
  error: string | null;
  newEnrollmentId: number | null;
}

export interface ExecutePromotionsResult {
  total: number;
  promoted: number;
  repeated: number;
  skipped: number;
  graduated: number;
  leftCount: number;
  failed: number;
  rows: PromotionResultRow[];
}

export interface PromotionHistoryRow {
  id: number;
  studentId: number;
  fromAcademicYearId: number;
  fromAcademicYearName: string;
  toAcademicYearId: number;
  toAcademicYearName: string;
  fromClassSectionName: string | null;
  toClassSectionName: string | null;
  decision: string;
  reason: string | null;
  promotedAt: string;
}
